import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, LogOut, User, Shield, Award, CreditCard, Wallet, Bell, FileText, Zap } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { studentAPI } from '../../utils/api';
import toast from 'react-hot-toast';
import styles from '../../styles/components/Navbar.module.css';

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pillStyle, setPillStyle] = useState({ opacity: 0 });
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const notifRef = useRef(null);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const navRefs = useRef({});
  const desktopNavRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setOpen(false); setDropdownOpen(false); }, [location]);

  useEffect(() => {
    if (!user || user.role === 'admin') return;
    const fetchCount = async () => {
      try {
        const res = await studentAPI.getUnreadNotificationCount();
        setUnreadCount(res.data.unreadCount || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Close notification popover on outside click
  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen]);

  const handleNotifToggle = async () => {
    if (notifOpen) { setNotifOpen(false); return; }
    setNotifOpen(true);
    setNotifLoading(true);
    try {
      const res = await studentAPI.getNotifications();
      setNotifications(res.data.notifications || []);
      setUnreadCount(0);
    } catch { /* ignore */ }
    setNotifLoading(false);
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const navLinks = user
    ? [
        { to: '/', label: 'Home', matchPrefix: false },
        ...(user.role !== 'admin' ? [
          { to: '/dashboard', label: 'Dashboard', matchPrefix: false },
          { to: '/dashboard/gamification', label: 'Stats', matchPrefix: true },
        ] : []),
        { to: '/internships', label: 'Internships', matchPrefix: true },
        { to: '/help', label: 'Help', matchPrefix: false },
        { to: '/about', label: 'About', matchPrefix: false },
      ]
    : [
        { to: '/', label: 'Home', matchPrefix: false },
        { to: '/internships', label: 'Internships', matchPrefix: true },
        { to: '/help', label: 'Help', matchPrefix: false },
        { to: '/verify', label: 'Verify Certificate', matchPrefix: true },
        { to: '/about', label: 'About', matchPrefix: false },
      ];

  const isActive = (link) => {
    if (link.matchPrefix) return location.pathname.startsWith(link.to);
    return location.pathname === link.to;
  };

  const activeLink = navLinks.find(isActive);
  const activeKey = activeLink ? activeLink.to : null;

  useLayoutEffect(() => {
    const measure = () => {
      const el = activeKey ? navRefs.current[activeKey] : null;
      if (!el || !desktopNavRef.current) {
        setPillStyle((s) => (s.opacity === 0 ? s : { ...s, opacity: 0 }));
        return;
      }
      const navBox = desktopNavRef.current.getBoundingClientRect();
      const linkBox = el.getBoundingClientRect();
      setPillStyle((s) => {
        const next = {
          opacity: 1,
          width: linkBox.width,
          transform: `translateX(${linkBox.left - navBox.left}px)`,
        };
        if (s.opacity === next.opacity && s.width === next.width && s.transform === next.transform) {
          return s;
        }
        return next;
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [activeKey]);

  const ICONS = { payment: '💳', certificate_issued: '🎓', submission_received: '📤', project_approved: '✅', project_rejected: '❌', enrollment: '📚', resubmit_requested: '🔄', general: '📣', streak_milestone: '🔥', badge_earned: '🏅', level_up: '⭐', xp_milestone: '🎯', daily_reminder: '📅' };
  const NOTIF_TABS = { streak_milestone: 'gamification', badge_earned: 'gamification', level_up: 'gamification', xp_milestone: 'gamification', daily_reminder: 'gamification' };

  return (
    <nav className={`${styles.nav} ${scrolled ? styles.navScrolled : ''}`} role="navigation" aria-label="Main navigation">
      <div className="container">
        <div className={styles.inner}>
          {/* Logo — always goes to / */}
          <Link to="/" className={styles.logo} aria-label="GetNextern home">
            <img src="/logo.png" alt="GetNextern" className={styles.logoImg} />
          </Link>

          {/* Desktop Nav */}
          <div className={styles.desktopNav} role="list" ref={desktopNavRef}>
            <span className={styles.navPill} style={pillStyle} aria-hidden="true" />
            {navLinks.map(link => {
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  ref={(el) => { navRefs.current[link.to] = el; }}
                  className={`${styles.navLink} ${isActive(link) ? styles.navLinkActive : ''}`}
                  role="listitem"
                  aria-current={isActive(link) ? 'page' : undefined}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Auth area */}
          <div className={styles.authArea}>
            <span className={styles.flag} title="Made in India">🇮🇳</span>
            {user ? (
              <>
                {user.role !== 'admin' && (
                  <div className={styles.notifWrapper} ref={notifRef}>
                    <button onClick={handleNotifToggle} className={styles.notificationBell} aria-label="Notifications">
                      <Bell size={20} />
                      {unreadCount > 0 && (
                        <span className={styles.notificationBadge}>{unreadCount > 99 ? '99+' : unreadCount}</span>
                      )}
                    </button>
                    {notifOpen && (
                      <div className={styles.notifDropdown}>
                        <div className={styles.notifHeader}>Notifications</div>
                        {notifLoading ? (
                          <div className={styles.notifLoading}>Loading...</div>
                        ) : notifications.length === 0 ? (
                          <div className={styles.notifEmpty}>
                            <span style={{ fontSize: 28 }}>🔔</span>
                            <p>No notifications yet.</p>
                          </div>
                        ) : (
                          <div className={styles.notifList}>
                            {notifications.map(n => {
                              const tab = NOTIF_TABS[n.type] || null;
                              return (
                                <div key={n.id}
                                  onClick={() => { if (tab) { setNotifOpen(false); navigate(`/dashboard/${tab}`); } }}
                                  className={`${styles.notifItem} ${n.is_read ? '' : styles.notifItemUnread}`}
                                  style={{ cursor: tab ? 'pointer' : 'default' }}
                                >
                                  <span className={styles.notifIcon}>{ICONS[n.type] || '📣'}</span>
                                  <div className={styles.notifBody}>
                                    <div className={styles.notifTitle}>{n.title}</div>
                                    <div className={styles.notifMsg}>{n.message}</div>
                                    <div className={styles.notifTime}>
                                      {new Date(n.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className={styles.dropdownWrapper}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={styles.userButton}
                  aria-expanded={dropdownOpen}
                  aria-haspopup="true"
                  aria-label="User menu"
                >
                  <div className={styles.userAvatar}>
                    {user.name?.charAt(0).toUpperCase()}
                  </div>
                  <span className={styles.userName}>
                    {user.name?.split(' ')[0]}
                  </span>
                  <ChevronDown size={16} color="#64748b" />
                </button>

                {dropdownOpen && (
                  <div className={styles.dropdown} role="menu">
                    <div className={styles.dropdownHeader}>
                      <div className={styles.dropdownName}>{user.name}</div>
                      <div className={styles.dropdownEmail}>{user.email}</div>
                    </div>
                    {user.role === 'admin' && (
                      <Link to="/admin" className={styles.dropdownItem} role="menuitem">
                        <Shield size={16} /> Admin Panel
                      </Link>
                    )}
                    {user.role !== 'admin' && (
                      <>
                        <Link to="/dashboard/certificates" className={styles.dropdownItem} role="menuitem">
                          <Award size={16} /> Certificates
                        </Link>
                        <Link to="/dashboard/payments" className={styles.dropdownItem} role="menuitem">
                          <CreditCard size={16} /> Payments
                        </Link>
                        <Link to="/dashboard/wallet-referrals" className={styles.dropdownItem} role="menuitem">
                          <Wallet size={16} /> Wallet & Referrals
                        </Link>
                        <Link to="/dashboard/gamification" className={styles.dropdownItem} role="menuitem">
                          <Zap size={16} /> Gamification
                        </Link>
                        <Link to="/dashboard/offer-letters" className={styles.dropdownItem} role="menuitem">
                          <FileText size={16} /> Offer Letter
                        </Link>
                        <Link to="/dashboard/profile" className={styles.dropdownItem} role="menuitem">
                          <User size={16} /> My Profile
                        </Link>
                      </>
                    )}
                    <div className={styles.dropdownDivider}>
                      <button onClick={handleLogout} className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`} role="menuitem">
                        <LogOut size={16} /> Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
              </>
            ) : (
              <>
                <Link to="/login" className="btn btn-ghost btn-sm">Login</Link>
                <Link to="/register" className="btn btn-primary btn-sm">Sign Up</Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setOpen(!open)}
            className={styles.mobileMenuBtn}
            aria-expanded={open}
            aria-label={open ? 'Close menu' : 'Open menu'}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className={styles.mobileMenu}>
          {navLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              className={`${styles.mobileNavLink} ${isActive(link) ? styles.mobileNavLinkActive : ''}`}
              onClick={() => setOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          {user && user.role !== 'admin' && (
            <>
              <div className={styles.mobileDivider} />
              <Link to="/dashboard/certificates" className={styles.mobileNavLink} onClick={() => setOpen(false)}>Certificates</Link>
              <Link to="/dashboard/payments" className={styles.mobileNavLink} onClick={() => setOpen(false)}>Payments</Link>
              <Link to="/dashboard/wallet-referrals" className={styles.mobileNavLink} onClick={() => setOpen(false)}>Wallet & Referrals</Link>
              <Link to="/dashboard/gamification" className={styles.mobileNavLink} onClick={() => setOpen(false)}>Stats</Link>
              <Link to="/dashboard/offer-letters" className={styles.mobileNavLink} onClick={() => setOpen(false)}>Offer Letter</Link>
              <Link to="/dashboard/profile" className={styles.mobileNavLink} onClick={() => setOpen(false)}>My Profile</Link>
            </>
          )}
          <div className={styles.mobileActions}>
            {user ? (
              <button onClick={handleLogout} className="btn btn-outline btn-full">Logout</button>
            ) : (
              <>
                <Link to="/login" className="btn btn-outline btn-full">Login</Link>
                <Link to="/register" className="btn btn-primary btn-full">Sign Up →</Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;